import axios from "axios";
import { toast } from "react-toastify";

// Constants
import { BASE_API_URL } from "../constants/api/api.constants";
import * as localStoageConsts from "../constants/localStorage/localStorage.constants";
import { BUSINESS_STATUS_VERIFIED } from "../constants/business-profile/business-profile.constants";

// Domain Models
import { Error as ApiError } from "../models/app/domain";

// Actions
import { logout } from "../redux/actions/app.actions";

// Utils
import * as localStorageUtils from "../utils";

// The error envelope every call/saga checks via `response.hasError`.
export class ErrorResponse {
  constructor(response) {
    this.hasError = true;
    this.error = new ApiError(response?.data || {});
  }
}

class ApiService {
  constructor() {
    let service = axios.create({
      baseURL: BASE_API_URL
    });
    service.interceptors.request.use(
      this.handleReqInterceptorSuccess,
      this.handleReqInterceptorError
    );
    service.interceptors.response.use(
      this.handleResInterceptorSuccess,
      this.handleResInterceptorError
    );
    this.service = service;
    this.isIssuingRefreshToken = false;
  }

  setReduxStore(store) {
    this.store = store;
  }

  handleReqInterceptorSuccess(request) {
    if (request.baseURL === BASE_API_URL && !request.headers.Authorization) {
      const token = localStorageUtils.getItem(localStoageConsts.TOKEN);

      if (token) {
        request.headers.Authorization = `Bearer ${token}`;
      }

      if (request.headerBranchId) {
        request.headers["x-branch"] = `${request.headerBranchId}`;
      }

      if (
        localStorageUtils.isEmpty(request.headerBranchId) &&
        localStorageUtils.getItem(localStoageConsts.ACTIVE_BRANCH)
      ) {
        request.headers["x-branch"] = `${localStorageUtils.getItem(
          localStoageConsts.ACTIVE_BRANCH
        )}`;
      }

      if (
        localStorageUtils.getItem(
          localStoageConsts.OPENED_SERVICE_REQUEST_DETAILS_BRANCH
        )
      ) {
        request.headers["x-branch"] = `${localStorageUtils.getItem(
          localStoageConsts.OPENED_SERVICE_REQUEST_DETAILS_BRANCH
        )}`;
      }

      if (
        localStorageUtils.getItem(
          localStoageConsts.OPENED_SERVICE_DETAILS_BRANCH
        )
      ) {
        request.headers["x-branch"] = `${localStorageUtils.getItem(
          localStoageConsts.OPENED_SERVICE_DETAILS_BRANCH
        )}`;
      }
    }
    return request;
  }

  handleReqInterceptorError = error => {
    console.log("request interceptor error", error.response);
    return Promise.reject(error);
  };

  handleResInterceptorSuccess(response) {
    return response;
  }

  handleResInterceptorError = async error => {
    const originalRequest = error.config;

    // Paywall: guarded routes reject with 403 SUBSCRIPTION_REQUIRED when the
    // business has no active subscription. Hard-redirect only post-onboarding
    // businesses: verified AND has/had a subscription. A never-subscribed
    // business mid-setup gets silent 403s instead — the onboarding checklist
    // (plan is the step before go-live) is its path to the plans page.
    // ponytail: fail-open when dashboardData is undefined (cold deep-link before
    // auth/onboarding resolves) — a not-yet-known business isn't force-paywalled;
    // the next guarded call after state loads re-checks.
    const { verificationStatus, subscriptionPlanName, currentSubscription } =
      this.store?.getState()?.app?.dashboardData || {};
    const everSubscribed =
      !localStorageUtils.isEmpty(subscriptionPlanName) ||
      !localStorageUtils.isEmpty(currentSubscription);
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.code === "SUBSCRIPTION_REQUIRED" &&
      verificationStatus === BUSINESS_STATUS_VERIFIED &&
      everSubscribed &&
      !window.location.pathname.startsWith("/subscription-plans")
    ) {
      window.location.href = "/subscription-plans?locked=1";
      return Promise.reject(error);
    }

    if (
      error.response &&
      error.response.status === 401 &&
      originalRequest.url !== `/auth/refresh` &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        if (!this.isIssuingRefreshToken) {
          await this.refreshAccessToken(originalRequest);
        } else {
          await this.waitForRefreshToken();
          const latestToken = localStorageUtils.getItem(localStoageConsts.TOKEN);
          if (!latestToken) {
            return Promise.reject(error);
          }
          originalRequest.headers.Authorization = `Bearer ${latestToken}`;
        }
        return this.service(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  };

  // Function for pausing code until refresh token is resolved in another api instance
  async waitForRefreshToken() {
    if (!this.isIssuingRefreshToken) return;
    await new Promise(resolve => setTimeout(resolve, 1000));
    await this.waitForRefreshToken();
  }

  async refreshAccessToken(request) {
    this.isIssuingRefreshToken = true;

    const refreshToken = localStorageUtils.getItem(
      localStoageConsts.REFRESH_TOKEN
    );

    if (!refreshToken) {
      this.isIssuingRefreshToken = false;
      toast.error("Session expired. Please log in again.");
      this.store.dispatch(logout());
      throw new Error("Missing refresh token");
    }

    try {
      const response = await axios.post(`${BASE_API_URL}/auth/refresh`, {
        refresh_token: refreshToken
      });

      const newToken = response.data?.auth?.access_token;
      const newRefreshToken = response.data?.auth?.refresh_token;

      if (!newToken || !newRefreshToken) {
        throw new Error("Invalid refresh response");
      }

      localStorageUtils.setItem(localStoageConsts.TOKEN, newToken);
      localStorageUtils.setItem(localStoageConsts.REFRESH_TOKEN, newRefreshToken);

      if (request) {
        request.headers.Authorization = `Bearer ${newToken}`;
      }
    } catch (error) {
      toast.error("Session expired. Please log in again.");
      this.store.dispatch(logout());
      throw error;
    } finally {
      this.isIssuingRefreshToken = false;
    }
  }

  async ensureValidToken() {
    const token = localStorageUtils.getItem(localStoageConsts.TOKEN);
    if (!token) return false;
    if (!localStorageUtils.isJwtExpired(token)) return true;
    try {
      await this.refreshAccessToken(null);
      return true;
    } catch {
      return false;
    }
  }

  async handleApiCall(apiCall) {
    try {
      const response = await apiCall();
      return response;
    } catch (error) {
      const response = error.response;
      // When a blob request fails (responseType: 'blob'), axios returns
      // the error body as a Blob too — even though the server sent the
      // usual JSON error envelope. ErrorResponse expects parsed JSON,
      // so unwrap the blob into JSON here. Without this, toast.error()
      // never gets the BE message and falls back to the generic label.
      if (response?.data instanceof Blob) {
        try {
          const text = await response.data.text();
          response.data = text ? JSON.parse(text) : {};
        } catch {
          // Body wasn't JSON (or text() failed) — leave .data empty so
          // ErrorResponse falls back to undefined message; the caller's
          // own fallback toast string will be used.
          response.data = {};
        }
      }
      return new ErrorResponse(response || {});
    }
  }

  async get(path, headerBranchId) {
    return this.handleApiCall(() =>
      this.service.request({
        method: "GET",
        url: path,
        headerBranchId: headerBranchId
      })
    );
  }

  async getBlob(path, headerBranchId) {
    return this.handleApiCall(() =>
      this.service.request({
        method: "GET",
        url: path,
        headerBranchId: headerBranchId,
        responseType: "blob"
      })
    );
  }

  async post(path, payload, headerBranchId) {
    return this.handleApiCall(() =>
      this.service.request({
        method: "POST",
        url: path,
        data: payload,
        headerBranchId: headerBranchId
      })
    );
  }

  async put(path, payload, headerBranchId) {
    return this.handleApiCall(() =>
      this.service.request({
        method: "PUT",
        url: path,
        data: payload,
        headerBranchId: headerBranchId
      })
    );
  }

  async patch(path, payload, headerBranchId) {
    return this.handleApiCall(() =>
      this.service.request({
        method: "PATCH",
        url: path,
        data: payload,
        headerBranchId: headerBranchId
      })
    );
  }

  async delete(path, headerBranchId) {
    return this.handleApiCall(() =>
      this.service.request({
        method: "DELETE",
        url: path,
        headerBranchId: headerBranchId
      })
    );
  }
}

export const apiService = new ApiService();
